import React, {useEffect, useState} from 'react';
import {
    Card,
    Table,
    Badge,
    Dropdown,
    Pagination,
    Form,
    Button,
    Row,
    Col,
} from 'react-bootstrap';
import {filterCateringHistory} from '../../../services/OrderService';

const svg1 = (
    <svg width='20px' height='20px' viewBox='0 0 24 24' version='1.1'>
        <g stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
            <rect x='0' y='0' width='24' height='24'></rect>
            <circle fill='#000000' cx='5' cy='12' r='2'></circle>
            <circle fill='#000000' cx='12' cy='12' r='2'></circle>
            <circle fill='#000000' cx='19' cy='12' r='2'></circle>
        </g>
    </svg>
);

const CateringList = props => {
    const {
        cateringList,
        getCaterings,
        pagination,
        pageItems = 10,
        onPageChange,
        onPageItemsChange,
        onDateFilter,
    } = props;

    const [selectedFilter, setSelectedFilter] = useState('All Status');
    const [filteredList, setFilteredList] = useState([]);
    const [currentPageItems, setCurrentPageItems] = useState(pageItems);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        getCaterings(1, currentPageItems);
    }, [getCaterings, currentPageItems]);

    // Auto-close toast after 3 seconds
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [showToast]);

    // Filter catering list based on selected status
    useEffect(() => {
        if (cateringList) {
            if (selectedFilter === 'All Status') {
                setFilteredList(cateringList);
            } else {
                const filtered = cateringList?.filter(
                    catering => catering.status === selectedFilter
                );
                setFilteredList(filtered || []);
            }
        }
    }, [cateringList, selectedFilter]);

    const handleDateFilter = () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            setToastMessage('From date cannot be later than To date');
            setShowToast(true);
            return;
        }

        onDateFilter(fromDate, toDate);
    };

    const resetDateFilter = () => {
        setFromDate('');
        setToDate('');
        onDateFilter('', '');
    };

    const handleFilterCateringHistory = status => {
        setSelectedFilter(status);

        if (status === 'All Status') {
            setFilteredList(cateringList);
        } else {
            // Filter locally first for immediate UI response
            const filtered = cateringList?.filter(
                catering => catering.status === status
            );
            setFilteredList(filtered || []);

            // If you have a separate API endpoint for filtering, uncomment below:

            filterCateringHistory(
                status,
                pagination.currentPage,
                currentPageItems,
                fromDate,
                toDate
            )
                .then(res => {
                    if (res?.data) {
                        setFilteredList(res.data);
                    }
                })
                .catch(err => console.error('Filter error:', err));
        }
    };

    const getBadgeVariant = status => {
        // Convert status to lowercase for case-insensitive comparison
        const statusLower = status?.toLowerCase() || '';

        // Check if status contains these keywords
        if (
            statusLower.includes('confirm') ||
            statusLower.includes('complete')
        ) {
            return 'success';
        } else if (statusLower.includes('cancel')) {
            return 'warning';
        } else if (statusLower.includes('reject')) {
            return 'danger';
        } else {
            return 'secondary';
        }
    };

    return (
        <>
            {showToast && (
                <div
                    className='alert alert-danger alert-dismissible fade show'
                    style={{
                        position: 'fixed',
                        top: 20,
                        right: 20,
                        zIndex: 9999,
                        minWidth: '300px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                    }}
                >
                    <strong>Error!</strong> {toastMessage}
                    <button
                        type='button'
                        className='close'
                        onClick={() => setShowToast(false)}
                    >
                        <span>&times;</span>
                    </button>
                </div>
            )}

            <div className='d-sm-flex mb-lg-4 mb-2'>
                <div className='mr-auto'>
                    <Form inline>
                        <Row>
                            <Col xs='auto'>
                                <Form.Group className='mb-2'>
                                    <Form.Label className='mr-2'>
                                        From:
                                    </Form.Label>
                                    <Form.Control
                                        type='date'
                                        value={fromDate}
                                        onChange={e =>
                                            setFromDate(e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs='auto'>
                                <Form.Group className='mb-2'>
                                    <Form.Label className='mr-2'>
                                        To:
                                    </Form.Label>
                                    <Form.Control
                                        type='date'
                                        value={toDate}
                                        onChange={e =>
                                            setToDate(e.target.value)
                                        }
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs='auto'>
                                <Button
                                    variant='primary'
                                    className='mb-2 mr-2'
                                    onClick={handleDateFilter}
                                >
                                    Filter
                                </Button>
                                <Button
                                    variant='outline-secondary'
                                    className='mb-2'
                                    onClick={resetDateFilter}
                                >
                                    Reset
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>

                <Dropdown className='dropdown mb-2 ml-auto mr-3'>
                    <Dropdown.Toggle
                        to='#'
                        className='i-false btn btn-primary btn-rounded light'
                        data-toggle='dropdown'
                        aria-expanded='false'
                    >
                        <i className='las la-bolt scale5 mr-3' />
                        {selectedFilter}
                        <i className='las la-angle-down ml-3' />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className='dropdown-menu dropdown-menu-center'>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#;'
                            onClick={() =>
                                handleFilterCateringHistory('All Status')
                            }
                        >
                            <span className='text-primary'>All Status</span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#;'
                            onClick={() =>
                                handleFilterCateringHistory(
                                    'Booking Confirmed'
                                )
                            }
                        >
                            <span className='text-primary'>
                                Booking Confirmed
                            </span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#;'
                            onClick={() =>
                                handleFilterCateringHistory(
                                    'Booking Completed'
                                )
                            }
                        >
                            <span className='text-success'>
                                Booking Completed
                            </span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#;'
                            onClick={() =>
                                handleFilterCateringHistory('Booking Rejected')
                            }
                        >
                            <span className='text-danger'>
                                Booking Rejected
                            </span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#;'
                            onClick={() =>
                                handleFilterCateringHistory(
                                    'Bid Created'
                                )
                            }
                        >
                            <span className='text-secondary'>Bid Created</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <Card>
                <Card.Header>
                    <Card.Title>Catering Order History</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Table>
                        <thead>
                            <tr>
                                <th>
                                    <strong>DELIVERY NO.</strong>
                                </th>
                                <th>
                                    <strong>DATE</strong>
                                </th>
                                <th>
                                    <strong>CUSTOMER</strong>
                                </th>
                                <th>
                                    <strong>AMOUNT</strong>
                                </th>
                                <th>
                                    <strong>STATUS</strong>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList && filteredList.length > 0 ? (
                                filteredList.map(each => (
                                    <tr
                                        key={
                                            each.booking_id ||
                                            Math.random()
                                                .toString(36)
                                                .substr(2, 9)
                                        }
                                    >
                                        <td>
                                            <strong>{each.booking_id}</strong>
                                        </td>
                                        <td>{each.date}</td>
                                        <td>{each.customer}</td>
                                        <td>SAR {each.budget}</td>
                                        <td>
                                            <Badge
                                                variant={getBadgeVariant(
                                                    each.status
                                                )}
                                            >
                                                {each.status}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan='5'
                                        className='text-center py-4'
                                    >
                                        <p className='text-muted'>
                                            {fromDate || toDate
                                                ? `No catering orders found for the selected date range ${
                                                      fromDate
                                                          ? `from ${fromDate}`
                                                          : ''
                                                  } ${
                                                      toDate
                                                          ? `to ${toDate}`
                                                          : ''
                                                  }.`
                                                : `No catering orders found for ${
                                                      selectedFilter !==
                                                      'All Status'
                                                          ? selectedFilter
                                                          : 'any'
                                                  } status.`}
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {cateringList && cateringList.length === 0 && (
                        <div className='text-center p-4'>
                            {fromDate || toDate ? (
                                <p>
                                    No catering orders found for the selected
                                    date range.
                                </p>
                            ) : (
                                <p>No catering orders found.</p>
                            )}
                        </div>
                    )}
                </Card.Body>

                <Card.Footer>
                    <div className='d-flex justify-content-between align-items-center'>
                        {/* Pagination controls */}
                        {pagination && pagination.totalPages > 0 && (
                            <Pagination className='mb-0'>
                                {/* First page button */}
                                <Pagination.First
                                    onClick={() => onPageChange(1)}
                                    disabled={pagination.currentPage === 1}
                                />

                                {/* Previous page button */}
                                <Pagination.Prev
                                    onClick={() =>
                                        onPageChange(pagination.currentPage - 1)
                                    }
                                    disabled={pagination.currentPage === 1}
                                />

                                {/* Page numbers */}
                                {[...Array(pagination.totalPages)].map(
                                    (_, index) => {
                                        const pageNumber = index + 1;
                                        // Show current page, first, last, and pages around current page
                                        if (
                                            pageNumber === 1 ||
                                            pageNumber ===
                                                pagination.totalPages ||
                                            (pageNumber >=
                                                pagination.currentPage - 1 &&
                                                pageNumber <=
                                                    pagination.currentPage + 1)
                                        ) {
                                            return (
                                                <Pagination.Item
                                                    key={pageNumber}
                                                    active={
                                                        pageNumber ===
                                                        pagination.currentPage
                                                    }
                                                    onClick={() =>
                                                        onPageChange(pageNumber)
                                                    }
                                                >
                                                    {pageNumber}
                                                </Pagination.Item>
                                            );
                                        } else if (
                                            pageNumber ===
                                                pagination.currentPage - 2 ||
                                            pageNumber ===
                                                pagination.currentPage + 2
                                        ) {
                                            return (
                                                <Pagination.Ellipsis
                                                    key={pageNumber}
                                                />
                                            );
                                        }
                                        return null;
                                    }
                                )}

                                {/* Next page button */}
                                <Pagination.Next
                                    onClick={() =>
                                        onPageChange(pagination.currentPage + 1)
                                    }
                                    disabled={
                                        pagination.currentPage ===
                                        pagination.totalPages
                                    }
                                />

                                {/* Last page button */}
                                <Pagination.Last
                                    onClick={() =>
                                        onPageChange(pagination.totalPages)
                                    }
                                    disabled={
                                        pagination.currentPage ===
                                        pagination.totalPages
                                    }
                                />
                            </Pagination>
                        )}

                        {/* Page size selector */}
                        <div className='d-flex align-items-center'>
                            <span className='mr-2'>Items per page:</span>
                            <Dropdown>
                                <Dropdown.Toggle
                                    variant='outline-primary'
                                    size='sm'
                                >
                                    {currentPageItems}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => {
                                            setCurrentPageItems(5);
                                            onPageItemsChange &&
                                                onPageItemsChange(5);
                                        }}
                                    >
                                        5
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => {
                                            setCurrentPageItems(10);
                                            onPageItemsChange &&
                                                onPageItemsChange(10);
                                        }}
                                    >
                                        10
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => {
                                            setCurrentPageItems(20);
                                            onPageItemsChange &&
                                                onPageItemsChange(20);
                                        }}
                                    >
                                        20
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => {
                                            setCurrentPageItems(50);
                                            onPageItemsChange &&
                                                onPageItemsChange(50);
                                        }}
                                    >
                                        50
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </Card.Footer>
            </Card>
        </>
    );
};

export default CateringList;
